/**
 * Subscription status helpers for paywall and grace period logic
 */

export interface SubscriptionAccessCheck {
  hasAccess: boolean;
  isGracePeriod: boolean;
  gracePeriodEndsAt: Date | null;
  isTrialing: boolean;
  isActive: boolean;
}

// Minimal organization interface for subscription checks
// Uses a subset of the full Organization type from the schema
export interface OrganizationSubscription {
  id: string;
  subscriptionStatus?: string | null;
  trialEndsAt?: string | null;
  subscriptionPeriodEnd?: string | null;
  plan?: string | null;
  billingPeriod?: string | null;
}

const GRACE_PERIOD_DAYS = 3;

/**
 * Check if user has access to the app based on subscription status
 * Returns access status with grace period info
 */
export function checkSubscriptionAccess(
  org: OrganizationSubscription | null
): SubscriptionAccessCheck {
  if (!org) {
    return {
      hasAccess: false,
      isGracePeriod: false,
      gracePeriodEndsAt: null,
      isTrialing: false,
      isActive: false,
    };
  }

  // Active subscription - full access
  if (org.subscriptionStatus === 'active') {
    return {
      hasAccess: true,
      isGracePeriod: false,
      gracePeriodEndsAt: null,
      isTrialing: false,
      isActive: true,
    };
  }

  // Valid trial - check if not expired
  if (org.subscriptionStatus === 'trialing' && org.trialEndsAt) {
    const trialEnd = new Date(org.trialEndsAt);
    const now = new Date();
    const trialValid = trialEnd > now;

    if (trialValid) {
      return {
        hasAccess: true,
        isGracePeriod: false,
        gracePeriodEndsAt: null,
        isTrialing: true,
        isActive: false,
      };
    }
  }

  // Check grace period (3 days after expiry)
  const expiryDate = org.trialEndsAt || org.subscriptionPeriodEnd;
  if (expiryDate) {
    const expiry = new Date(expiryDate);
    const gracePeriodEnd = new Date(expiry);
    gracePeriodEnd.setDate(gracePeriodEnd.getDate() + GRACE_PERIOD_DAYS);

    const now = new Date();
    const inGracePeriod = now < gracePeriodEnd;

    if (inGracePeriod) {
      return {
        hasAccess: true, // Read-only access during grace period
        isGracePeriod: true,
        gracePeriodEndsAt: gracePeriodEnd,
        isTrialing: false,
        isActive: false,
      };
    }
  }

  // No valid subscription, trial expired, and past grace period
  return {
    hasAccess: false,
    isGracePeriod: false,
    gracePeriodEndsAt: null,
    isTrialing: false,
    isActive: false,
  };
}

/**
 * Calculate grace period end date from expiry date
 */
export function calculateGracePeriodEnd(expiryDate: Date | string): Date {
  const expiry = typeof expiryDate === 'string' ? new Date(expiryDate) : expiryDate;
  const gracePeriodEnd = new Date(expiry);
  gracePeriodEnd.setDate(gracePeriodEnd.getDate() + GRACE_PERIOD_DAYS);
  return gracePeriodEnd;
}

/**
 * Calculate days remaining in grace period
 */
export function getDaysRemainingInGracePeriod(gracePeriodEnd: Date): number {
  const now = new Date();
  const msRemaining = gracePeriodEnd.getTime() - now.getTime();
  const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
  return Math.max(0, daysRemaining);
}

/**
 * Check if trial has expired
 */
export function isTrialExpired(org: OrganizationSubscription | null): boolean {
  if (!org || org.subscriptionStatus !== 'trialing' || !org.trialEndsAt) {
    return false;
  }

  const trialEnd = new Date(org.trialEndsAt);
  const now = new Date();
  return now >= trialEnd;
}

/**
 * Check if subscription is canceled
 */
export function isSubscriptionCanceled(org: OrganizationSubscription | null): boolean {
  return org?.subscriptionStatus === 'canceled';
}

/**
 * Detect card-free trial: trialing status without a Stripe subscription.
 */
export function isCardFreeTrial(
  organization: {
    subscriptionStatus?: string | null;
    stripeSubscriptionId?: string | null;
  } | null
): boolean {
  return organization?.subscriptionStatus === 'trialing' && !organization?.stripeSubscriptionId;
}

/**
 * Get context-aware messaging for subscribe page
 */
export function getSubscribePageContext(
  org: OrganizationSubscription | null,
  isOnboarding: boolean
): {
  heading: string;
  subheading: string;
  ctaText: string;
} {
  if (isOnboarding) {
    return {
      heading: 'Choose your plan',
      subheading: 'Start your 7-day free trial. No credit card required.',
      ctaText: 'Start Free Trial',
    };
  }

  if (!org) {
    return {
      heading: 'Subscribe to continue',
      subheading: 'Select a plan to access SendGuard.',
      ctaText: 'Subscribe Now',
    };
  }

  const expired = isTrialExpired(org);
  const canceled = isSubscriptionCanceled(org);

  if (expired && org.trialEndsAt) {
    const expiryDate = new Date(org.trialEndsAt).toLocaleDateString();
    return {
      heading: 'Your trial has ended',
      subheading: `Your 7-day trial ended on ${expiryDate}. Choose a plan to continue using SendGuard.`,
      ctaText: 'Subscribe Now',
    };
  }

  if (canceled) {
    return {
      heading: 'Reactivate your subscription',
      subheading: 'Your subscription was cancelled. Reactivate to continue using SendGuard.',
      ctaText: 'Reactivate Subscription',
    };
  }

  return {
    heading: 'Subscribe to continue',
    subheading: 'Select a plan to access SendGuard.',
    ctaText: 'Subscribe Now',
  };
}
